// Los acuerdos de devolución: los préstamos que quedaron sin interés.
//
// Cuando alguien lleva años pagando interés, el prestamista a veces cuadra con
// él: «devuélvame el capital y no le cobro más». Eso se registra poniéndole 0%
// al préstamo, y desde ese momento **el programa deja de mencionarlo**.
//
// No es un fallo del cálculo, es una consecuencia: «Por cobrar» muestra lo que
// hay que cobrar este periodo, y en un préstamo al 0% eso es cero. Morosos mide
// quién no está pagando lo que debe, y no debe nada. Así que el acuerdo sale
// perfecto de las dos pantallas mientras nadie devuelve un peso.
//
// Medido el 2026-08-16 en una cartera real: 21 acuerdos, $74.380.000 por
// recuperar, y ocho sin una sola devolución. Uno de $25.000.000 llevaba 19
// meses sin que ninguna pantalla lo nombrara.
//
// Esta pantalla no los llama morosos —no lo son, se pactó así— ni les inventa
// una fecha de vencimiento. Contesta la única pregunta que hoy nadie contesta:
// ¿quién no me está devolviendo?

const DIA = 86400000;

// A partir de cuántos días sin recibir nada se considera que el acuerdo se
// enfrió. Un mes, porque es el ritmo natural de estos acuerdos aunque no se
// haya pactado por escrito.
//
// Se usa también para no alarmar por un acuerdo recién hecho: el que se pactó
// la semana pasada no ha «dejado de devolver», simplemente no le ha dado tiempo.
export const DIAS_PARA_ENFRIARSE = 30;

const aFecha = (f) => {
  if (!f) return null;
  const d = f instanceof Date ? f : new Date(String(f).length <= 10 ? `${f}T00:00:00` : f);
  return isNaN(d.getTime()) ? null : d;
};

const diasEntre = (desde, hasta) => {
  const a = aFecha(desde), b = aFecha(hasta);
  if (!a || !b) return null;
  // Días enteros. Comparar instantes hace que «hoy» parezca ayer según la hora.
  return Math.floor(b.getTime() / DIA) - Math.floor(a.getTime() / DIA);
};

// Un acuerdo, con lo que hace falta para decidir qué hacer con él.
export function estadoDelAcuerdo(prestamo, pagos, hasta = new Date()) {
  if (!prestamo) return null;

  const propios = (pagos || []).filter(p => p && p.loan_id === prestamo.id);
  const devuelto = propios.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const prestado = Number(prestamo.amount) || 0;

  const fechas = propios
    .map(p => aFecha(p.payment_date))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const ultima = fechas.length ? fechas[fechas.length - 1] : null;

  const diasSinDevolver = ultima
    ? diasEntre(ultima, hasta)
    : diasEntre(prestamo.start_date, hasta);

  const falta = Math.max(0, prestado - devuelto);
  const porcentaje = prestado > 0 ? Math.min(100, Math.round(devuelto / prestado * 100)) : 0;

  return {
    prestamo,
    prestado,
    devuelto,
    falta,
    porcentaje,
    devoluciones: propios.length,
    ultimaDevolucion: ultima,
    // Cuánto lleva sin devolver. Si nunca devolvió, se cuenta desde que se
    // prestó: es la cifra que dice si el acuerdo nació muerto o es reciente.
    diasSinDevolver,
    grupo: agrupar({ falta, devoluciones: propios.length, diasSinDevolver }),
  };
}

function agrupar({ falta, devoluciones, diasSinDevolver }) {
  // Ya devolvió todo. No es un pendiente: es un préstamo que debería cerrarse,
  // y decirlo vale más que dejarlo en la lista sumando un cero.
  if (falta <= 0) return 'completado';

  const frio = diasSinDevolver === null || diasSinDevolver > DIAS_PARA_ENFRIARSE;

  if (devoluciones === 0) {
    // El de la semana pasada no ha dejado de devolver: todavía no empieza.
    // Meterlo con los abandonados es como se pierde la confianza en la lista.
    return frio ? 'sin_empezar' : 'recien_pactado';
  }

  return frio ? 'enfriado' : 'devolviendo';
}

// El orden en que hay que leerlos: primero lo que duele.
export const GRUPOS = [
  ['sin_empezar',    'No han devuelto nada'],
  ['enfriado',       'Llevan tiempo sin devolver'],
  ['devolviendo',    'Van devolviendo'],
  ['recien_pactado', 'Recién pactados'],
  ['completado',     'Ya devolvieron todo'],
];

// Toda la lista, agrupada y ordenada.
//
// `prestamos` son los del prestamista con su tasa; se filtran aquí los que
// están en acuerdo para que la pantalla no tenga que saber qué es un acuerdo.
export function acuerdosDeDevolucion(prestamos, pagos, hasta = new Date()) {
  const enAcuerdo = (prestamos || []).filter(p =>
    p && p.status === 'active'
    && Number(p.interest_rate || 0) === 0
    // La cuota fija al 0% no existe como acuerdo: ahí el total se pactó desde
    // el principio y el 0% sería un préstamo sin ganancia, otra cosa.
    && p.modalidad !== 'cuota_fija');

  const estados = enAcuerdo
    .map(p => estadoDelAcuerdo(p, pagos, hasta))
    .filter(Boolean);

  const grupos = GRUPOS.map(([clave, titulo]) => ({
    clave,
    titulo,
    acuerdos: estados
      .filter(a => a.grupo === clave)
      // Dentro de cada grupo, primero el que lleva más tiempo callado; a igual
      // tiempo, el de más plata.
      .sort((a, b) => (b.diasSinDevolver || 0) - (a.diasSinDevolver || 0)
                   || b.falta - a.falta),
  })).filter(g => g.acuerdos.length > 0);

  return {
    grupos,
    cuantos: estados.length,
    prestado: estados.reduce((s, a) => s + a.prestado, 0),
    devuelto: estados.reduce((s, a) => s + a.devuelto, 0),
    falta: estados.reduce((s, a) => s + a.falta, 0),
  };
}
